import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import './MobileStrategy.css';

// Data & hooks
import useCampaign from '../../../hooks/useCampaign';
import useMobileStrategy from '../../../hooks/useMobileStrategy';
import useApi from '../../../hooks/useApi';
import useTestMode from '../../../hooks/useTestMode.js';
import useLanguage from '../../../hooks/useLanguage';

// Components
import MobileCampaignSelector from '../MobileCampaignSelector/MobileCampaignSelector';
import PostCarousel from './PostCarousel';
import { Skeleton } from '../../effects';

// Icons
import iconAi from '../../../assets/images/icons/icon-ai.svg';
import API_CONFIG from '../../../config/api';

const ACTIVE_CAMPAIGN_STORAGE_KEY = 'mobile_active_campaign';
const FORCE_WEEK_JOB_STORAGE_KEY = 'mobile_force_week_job';
const FORCE_WEEK_JOB_MAX_AGE_MS = 15 * 60 * 1000;

// Remove dev mode function and state
// const shouldUseDevMode = () => { ... };

const MobileStrategy = ({ targetCampaignId = null, onScreenChange, onCampaignCreated }) => {
  // Hooks
  const campaignHook = useCampaign();
  const strategyHook = useMobileStrategy();
  const { get: apiGet } = useApi();
  const { isTestModeEnabled } = useTestMode();
  const { t, language } = useLanguage();

  // Extract data from hooks
  const campaigns = campaignHook?.campaigns || [];
  const currentCampaign = campaignHook?.currentCampaign || null;
  const loadCampaigns = campaignHook?.loadCampaigns || (() => Promise.resolve());
  const switchCampaign = campaignHook?.switchCampaign || (() => {});
  const updateCampaign = campaignHook?.updateCampaign || null;
  const backendLoading = campaignHook?.loading || false;
  const backendError = campaignHook?.error || null;

  // Strategy data from hook
  const strategyData = strategyHook.strategyData;
  const isGeneratingStrategy = strategyHook.isGeneratingStrategy;
  const strategyError = strategyHook.error;
  const strategyLoading = strategyHook.loading;
  const mediaFiles = strategyHook.mediaFiles;
  const strategyImages = strategyHook.strategyImages;

  // Image preview state with carousel support
  const [imagePreview, setImagePreview] = useState(null);
  const [imageGallery, setImageGallery] = useState([]);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  // Strategy parameters - mobile optimized
  const [selectedWeek, setSelectedWeek] = useState(0);

  // Posts fetched via API to enhance strategy previews
  const [campaignPosts, setCampaignPosts] = useState([]);
  const [postsLoading, setPostsLoading] = useState(false);
  const [postsError, setPostsError] = useState(null);

  // Campaign status toggle state
  const [isStatusDropdownOpen, setIsStatusDropdownOpen] = useState(false);
  const statusDropdownRef = useRef(null);

  // Refresh management for forced week regeneration
  const [postsRefreshKey, setPostsRefreshKey] = useState(0);
  const [pendingWeekJob, setPendingWeekJob] = useState(null);
  const [pendingJobCountdown, setPendingJobCountdown] = useState(null);
  const pendingJobTimeoutRef = useRef(null);
  const pendingJobIntervalRef = useRef(null);
  const pendingJobCleanupRef = useRef(null);
  const lastActiveCampaignIdRef = useRef(null);

  // Remove all mock data and dev mode logic

  const weekDateFormatter = useMemo(() => {
    try {
      return new Intl.DateTimeFormat(language || 'it', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    } catch (error) {
      console.warn('[MobileStrategy] Falling back to default week date formatter', error);
      return new Intl.DateTimeFormat('it', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    }
  }, [language]);

  const formatWeekDate = useCallback((date) => {
    if (!date) return null;
    try {
      const parsed = new Date(date);
      if (Number.isNaN(parsed.getTime())) {
        return null;
      }
      return weekDateFormatter.format(parsed);
    } catch (error) {
      console.warn('[MobileStrategy] Unable to format week date', { date, error });
      return null;
    }
  }, [weekDateFormatter]);

  const timeFormatter = useMemo(() => {
    try {
      return new Intl.DateTimeFormat(language || 'it', {
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      console.warn('[MobileStrategy] Falling back to default time formatter', error);
      return new Intl.DateTimeFormat('it', {
        hour: '2-digit',
        minute: '2-digit'
      });
    }
  }, [language]);

  const statusLabels = useMemo(() => ({
    draft: t('strategy.status.options.draft'),
    active: t('strategy.status.options.active'),
    paused: t('strategy.status.options.paused'),
    completed: t('strategy.status.options.completed')
  }), [t]);

  const getStatusLabel = useCallback((status) => {
    if (status && statusLabels[status]) {
      return statusLabels[status];
    }
    return statusLabels.draft;
  }, [statusLabels]);

  const statusOptions = useMemo(() => ([
    { value: 'draft', label: statusLabels.draft },
    { value: 'active', label: statusLabels.active },
    { value: 'paused', label: statusLabels.paused },
    { value: 'completed', label: statusLabels.completed }
  ]), [statusLabels]);

  const getNormalizedPostId = useCallback((post) => {
    if (!post || typeof post !== 'object') return null;
    const directId = post.id ?? post._id ?? post.post_id ?? post.postId ?? post.uuid;
    if (directId !== undefined && directId !== null) {
      return String(directId);
    }

    const nestedId = post?.metadata?.id ?? post?.metadata?.post_id ?? post?.metadata?.postId;
    if (nestedId !== undefined && nestedId !== null) {
      return String(nestedId);
    }

    return null;
  }, []);

  const getPostWeekIndex = useCallback((post) => {
    if (!post || typeof post !== 'object') return 0;

    const candidates = [
      post.week_index,
      post.weekIndex,
      post.week_number,
      post.weekNumber,
      post.strategy_week,
      post.strategyWeek,
      post.campaign_week_index,
      post.campaignWeekIndex,
      post?.metadata?.week_index,
      post?.metadata?.weekIndex,
      post?.schedule?.week_index,
      post?.schedule?.weekIndex
    ];

    for (const candidate of candidates) {
      if (candidate === undefined || candidate === null) continue;

      if (typeof candidate === 'object' && '$numberInt' in candidate) {
        const parsedNumberInt = Number(candidate.$numberInt);
        if (Number.isFinite(parsedNumberInt)) {
          return parsedNumberInt;
        }
        continue;
      }

      const parsed = typeof candidate === 'string' ? Number.parseInt(candidate, 10) : Number(candidate);
      if (Number.isFinite(parsed)) {
        return parsed;
      }
    }

    return 0;
  }, []);

  const getPostScheduledDate = useCallback((post) => {
    if (!post) return null;
    const rawValue =
      post.scheduledDate ||
      post.scheduled_time ||
      post.scheduledTime ||
      post.scheduled_at ||
      post.scheduledAt;

    if (!rawValue) return null;

    const normalized = typeof rawValue === 'object' && rawValue.$date
      ? rawValue.$date
      : rawValue;

    const date = new Date(normalized);
    return Number.isNaN(date.getTime()) ? null : date;
  }, []);

  const renderStrategyContentSkeleton = () => {
    const selectorPlaceholders = Array.from({ length: 3 });
    const textPlaceholders = Array.from({ length: 3 });
    const thumbnailPlaceholders = Array.from({ length: 4 });
    const weekPostPlaceholders = Array.from({ length: 3 });

    return (
      <>
        <div className="mobile-strategy-block">
          <div className="block-header">
            <Skeleton width="40%" height={18} shimmer />
          </div>
          <div className="block-content">
            {textPlaceholders.map((_, index) => (
              <Skeleton
                key={`general-text-${index}`}
                width={`${90 - index * 10}%`}
                height={14}
                shimmer
                style={{ marginBottom: 8 }}
              />
            ))}
          </div>
        </div>

        <div className="mobile-strategy-block">
          <div className="block-header">
            <Skeleton width="50%" height={18} shimmer />
          </div>
          <div className="block-content" style={{ width: '100%' }}>
            <Skeleton width="60%" height={14} shimmer />
            <div className="mobile-ai-project-thumbnails" style={{ marginTop: 12 }}>
              <div className="mobile-ai-thumbnails-row" style={{ display: 'flex', gap: 8 }}>
                {thumbnailPlaceholders.map((_, index) => (
                  <Skeleton
                    key={`style-thumb-${index}`}
                    variant="rounded"
                    width={64}
                    height={64}
                    shimmer
                  />
                ))}
              </div>
            </div>
            <Skeleton width="45%" height={16} shimmer style={{ marginTop: 20 }} />
            <Skeleton width="70%" height={12} shimmer style={{ marginTop: 8 }} />
            <Skeleton width="80%" height={12} shimmer style={{ marginTop: 8 }} />
          </div>
        </div>

        <div className="mobile-strategy-block mobile-weeks-container">
          <div className="week-selector">
            <div className="week-selector-buttons" style={{ display: 'flex', gap: 8 }}>
              {selectorPlaceholders.map((_, index) => (
                <Skeleton
                  key={`week-btn-${index}`}
                  variant="rounded"
                  width={90}
                  height={32}
                  shimmer
                />
              ))}
            </div>
          </div>
          <div className="week-content">
            <div className="week-header-info" style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
              <Skeleton width="40%" height={14} shimmer />
              <Skeleton width={140} height={32} shimmer />
            </div>
            <div className="week-description-container">
              <Skeleton width="90%" height={12} shimmer style={{ marginTop: 8 }} />
              <Skeleton width="80%" height={12} shimmer style={{ marginTop: 8 }} />
            </div>
            <div className="week-posts">
              <Skeleton width="40%" height={16} shimmer />
              <div className="week-posts-skeleton-list" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {weekPostPlaceholders.map((_, index) => (
                  <Skeleton
                    key={`week-post-${index}`}
                    variant="rounded"
                    width="100%"
                    height={120}
                    shimmer
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </>
    );
  };

  useEffect(() => {
    let isActive = true;

    const fetchCampaignPosts = async () => {
      if (!currentCampaign?.id) {
        if (isActive) {
          setCampaignPosts([]);
          setPostsError(null);
        }
        return;
      }

      setPostsLoading(true);
      setPostsError(null);

      try {
        const query = new URLSearchParams({
          campaign_id: currentCampaign.id,
          limit: '1000',
          offset: '0'
        });

        const response = await apiGet(`${API_CONFIG.ENDPOINTS.POSTS.LIST}?${query.toString()}`);
        if (!isActive) return;

        const posts = Array.isArray(response?.posts) ? response.posts : [];
        const normalizedPosts = posts.map((post) => {
          const normalizedId = getNormalizedPostId(post);
          return normalizedId ? { ...post, id: normalizedId } : post;
        });

        setCampaignPosts(normalizedPosts);
      } catch (error) {
        if (!isActive) return;
        console.error('[MobileStrategy] Failed to load campaign posts:', error);
        setCampaignPosts([]);
        setPostsError(error?.message || t('strategy.errors.postsLoad'));
      } finally {
        if (isActive) {
          setPostsLoading(false);
        }
      }
    };

    fetchCampaignPosts();

    return () => {
      isActive = false;
    };
  }, [apiGet, currentCampaign?.id, getNormalizedPostId, strategyData?.weeks?.length, postsRefreshKey, t]);

  const strategyWeeks = Array.isArray(strategyData?.weeks) ? strategyData.weeks : [];
  const strategyWeeksCount = strategyWeeks.length;
  const extraWeekIndex = strategyWeeksCount;

  const lastWeekEndDate = useMemo(() => {
    if (!strategyWeeksCount) return null;
    const lastWeek = strategyWeeks[strategyWeeksCount - 1];
    const rawEndDate = lastWeek?.end_date || lastWeek?.endDate;
    if (!rawEndDate) return null;
    const parsed = new Date(rawEndDate);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }, [strategyWeeks, strategyWeeksCount]);

  const isExtraWeekSelected = strategyWeeksCount > 0 && selectedWeek === extraWeekIndex;

  const postsById = useMemo(() => {
    const map = new Map();
    campaignPosts.forEach((post) => {
      const postId = getNormalizedPostId(post);
      if (postId) {
        map.set(postId, post);
      }
    });
    return map;
  }, [campaignPosts, getNormalizedPostId]);

  const postsByWeek = useMemo(() => {
    const map = new Map();
    campaignPosts.forEach((post) => {
      const scheduledDate = getPostScheduledDate(post);
      let weekIndex = getPostWeekIndex(post);
      const hasValidWeekIndex = Number.isFinite(weekIndex) && weekIndex >= 0;
      const isBeyondGeneratedWeeks = strategyWeeksCount > 0 && hasValidWeekIndex && weekIndex >= strategyWeeksCount;
      const isBeyondByDate = strategyWeeksCount > 0 && lastWeekEndDate && scheduledDate && scheduledDate > lastWeekEndDate;

      if (isBeyondGeneratedWeeks || isBeyondByDate) {
        weekIndex = extraWeekIndex;
      } else if (!hasValidWeekIndex) {
        weekIndex = 0;
      }

      const normalizedWeekIndex = Number.isFinite(weekIndex) && weekIndex >= 0 ? weekIndex : 0;

      if (!map.has(normalizedWeekIndex)) {
        map.set(normalizedWeekIndex, []);
      }
      map.get(normalizedWeekIndex).push(post);
    });
    return map;
  }, [campaignPosts, extraWeekIndex, getPostScheduledDate, getPostWeekIndex, lastWeekEndDate, strategyWeeksCount]);

  const weekPosts = useMemo(() => {
    const strategyWeekPosts = strategyWeeks[selectedWeek]?.posts || [];
    const fallbackWeekPosts = postsByWeek.get(selectedWeek) || [];

    if (!Array.isArray(strategyWeekPosts) || strategyWeekPosts.length === 0) {
      return fallbackWeekPosts;
    }

    const normalizeMediaArray = (value) => {
      if (!value) return [];
      if (Array.isArray(value)) return value;
      return [value];
    };

    const mergedPosts = strategyWeekPosts.map((post) => {
      const postId = getNormalizedPostId(post);
      const apiPost = postId ? postsById.get(postId) : null;

      if (!apiPost) {
        return post;
      }

      const mediaFromApi = normalizeMediaArray(apiPost.media_urls || apiPost.images || apiPost.mediaUrls);
      const mediaFromStrategy = normalizeMediaArray(post.media_urls || post.images || post.mediaUrls);
      const mergedMedia = mediaFromApi.length > 0 ? mediaFromApi : mediaFromStrategy;

      return {
        ...post,
        ...apiPost,
        id: postId,
        media_urls: mergedMedia,
        images: mergedMedia,
        versions: Array.isArray(apiPost.versions) && apiPost.versions.length > 0 ? apiPost.versions : post.versions,
        current_version: apiPost.current_version || apiPost.currentVersion || post.current_version,
        current_version_id: apiPost.current_version_id || apiPost.currentVersionId || post.current_version_id
      };
    });

    const knownIds = new Set(mergedPosts.map((post) => getNormalizedPostId(post)).filter(Boolean));
    fallbackWeekPosts.forEach((post) => {
      const postId = getNormalizedPostId(post);
      if (postId && !knownIds.has(postId)) {
        mergedPosts.push(post);
      }
    });

    return mergedPosts;
  }, [getNormalizedPostId, postsById, postsByWeek, selectedWeek, strategyWeeks]);

  const selectedWeekData = useMemo(() => {
    if (!strategyWeeks.length) {
      return null;
    }
    return strategyWeeks[selectedWeek] || null;
  }, [selectedWeek, strategyWeeks]);

  const selectedWeekPeriod = useMemo(() => {
    if (isExtraWeekSelected) {
      const formattedStart = lastWeekEndDate ? formatWeekDate(lastWeekEndDate) : null;
      if (formattedStart) {
        return t('strategy.week.extraPeriod', { start: formattedStart, defaultValue: `Dal ${formattedStart} in poi` });
      }
      return t('strategy.week.extraLabel', { defaultValue: 'Settimana extra' });
    }

    const startDate = selectedWeekData?.start_date ? formatWeekDate(selectedWeekData.start_date) : null;
    const endDate = selectedWeekData?.end_date ? formatWeekDate(selectedWeekData.end_date) : null;

    if (startDate && endDate) {
      return t('strategy.week.period', { start: startDate, end: endDate, defaultValue: `${startDate} → ${endDate}` });
    }

    return t('strategy.week.datePlaceholder', { defaultValue: 'Periodo non disponibile' });
  }, [formatWeekDate, isExtraWeekSelected, lastWeekEndDate, selectedWeekData, t]);

  const weekSteps = useMemo(() => {
    if (!strategyWeeks.length) {
      return [];
    }

    const buildWeekTitle = (label, number) => {
      const raw = (label ?? '').toString().trim();
      if (!raw) return `Settimana ${number}`;
      const normalized = raw.toLowerCase();
      if (normalized.includes('settimana')) return raw;
      if (/^\d+$/.test(raw)) {
        return `Settimana ${raw}`;
      }
      return `Settimana ${number}`;
    };

    const steps = strategyWeeks.map((_, index) => {
      const translated = t('strategy.week.short', {
        number: index + 1,
        defaultValue: `Settimana ${index + 1}`
      });

      return {
        step: index + 1,
        title: buildWeekTitle(translated, index + 1)
      };
    });

    steps.push({
      step: strategyWeeks.length + 1,
      title: t('strategy.week.extra', { defaultValue: 'Extra' }) || 'Extra',
      isExtra: true
    });

    return steps;
  }, [strategyWeeks, t]);

  // Load campaign data on mount and when campaign changes
  useEffect(() => {
    if (!campaigns || campaigns.length === 0) {
      loadCampaigns();
    }

    if (currentCampaign && currentCampaign.id) {
      loadCampaignStrategy(currentCampaign.id);
    }
  }, [currentCampaign?.id, loadCampaigns]);

  // Seleziona automaticamente la campagna target quando viene passata
  useEffect(() => {
    if (targetCampaignId && campaigns.length > 0) {
      // Cerca la campagna corrispondente all'ID target
      const targetCampaign = campaigns.find(c => {
        const campaignId = c?.id || c?._id;

        // Normalizza l'ID della campagna per il confronto
        const normalizedCampaignId = campaignId?.$oid ||
                                     (typeof campaignId === 'object' ? JSON.stringify(campaignId) : String(campaignId));

        const normalizedTargetId = typeof targetCampaignId === 'object' ? JSON.stringify(targetCampaignId) : String(targetCampaignId);

        return normalizedCampaignId === normalizedTargetId;
      });

      if (targetCampaign) {
        // Solo se la campagna corrente è diversa da quella target
        const currentId = currentCampaign?.id || currentCampaign?._id;
        const normalizedCurrentId = currentId?.$oid ||
                                   (typeof currentId === 'object' ? JSON.stringify(currentId) : String(currentId));

        const normalizedTargetId = typeof targetCampaignId === 'object' ? JSON.stringify(targetCampaignId) : String(targetCampaignId);

        if (normalizedCurrentId !== normalizedTargetId) {
          switchCampaign(targetCampaign);
        }
      }
    }
  }, [targetCampaignId, campaigns, switchCampaign, currentCampaign]);

  // Reset selected week when strategy data changes
  useEffect(() => {
    setSelectedWeek(0);
  }, [strategyData]);

  useEffect(() => {
    const normalizeCampaignId = (campaign) => {
      if (!campaign) return null;
      const rawId = campaign.id ?? campaign._id;
      if (rawId === undefined || rawId === null) {
        return null;
      }

      if (typeof rawId === 'object') {
        if (rawId.$oid) return String(rawId.$oid);
        try {
          return JSON.stringify(rawId);
        } catch (error) {
          console.warn('[MobileStrategy] Unable to normalize campaign id', error);
          return null;
        }
      }

      return String(rawId);
    };

    if (!currentCampaign) {
      lastActiveCampaignIdRef.current = null;
      try {
        sessionStorage.removeItem(ACTIVE_CAMPAIGN_STORAGE_KEY);
      } catch (error) {
        console.warn('[MobileStrategy] Unable to clear active campaign storage', error);
      }
      return;
    }

    const normalizedId = normalizeCampaignId(currentCampaign);
    if (!normalizedId) {
      return;
    }

    if (lastActiveCampaignIdRef.current === normalizedId) {
      return;
    }

    lastActiveCampaignIdRef.current = normalizedId;

    const campaignMeta = {
      id: normalizedId,
      name: currentCampaign.name || '',
      status: currentCampaign.status || ''
    };

    try {
      sessionStorage.setItem(ACTIVE_CAMPAIGN_STORAGE_KEY, JSON.stringify(campaignMeta));
    } catch (error) {
      console.warn('[MobileStrategy] Unable to persist active campaign', error);
    }

    window.dispatchEvent(new CustomEvent('mobileActiveCampaignChanged', { detail: campaignMeta }));
  }, [currentCampaign]);

  // Close status dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (statusDropdownRef.current && !statusDropdownRef.current.contains(event.target)) {
        setIsStatusDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const loadCampaignStrategy = async (campaignId) => {
    try {
      await strategyHook.loadStrategyFromCampaign(campaignId);
    } catch (error) {
      console.error('[MobileStrategy] Error loading strategy:', error);
    }
  };

  const handleCampaignSelect = useCallback((campaign) => {
    switchCampaign(campaign);
  }, [switchCampaign]);

  const handleCreateCampaign = useCallback(() => {
    if (onScreenChange) {
      onScreenChange('new-campaign');
    }
  }, [onScreenChange]);

  const cancelJobTimers = useCallback(() => {
    if (pendingJobTimeoutRef.current) {
      clearTimeout(pendingJobTimeoutRef.current);
      pendingJobTimeoutRef.current = null;
    }
    if (pendingJobIntervalRef.current) {
      clearInterval(pendingJobIntervalRef.current);
      pendingJobIntervalRef.current = null;
    }
    if (pendingJobCleanupRef.current) {
      clearTimeout(pendingJobCleanupRef.current);
      pendingJobCleanupRef.current = null;
    }
  }, []);

  const refreshAfterJob = useCallback(async (jobDetail, { manual = false } = {}) => {
    if (!jobDetail?.campaignId || !currentCampaign?.id || jobDetail.campaignId !== currentCampaign.id) {
      return;
    }

    cancelJobTimers();

    setPendingWeekJob((prev) => prev ? { ...prev, status: manual ? 'manual-refresh' : 'refreshing', startedAt: Date.now() } : prev);

    try {
      await loadCampaignStrategy(jobDetail.campaignId);
      setPostsRefreshKey((prev) => prev + 1);

      try {
        sessionStorage.removeItem(FORCE_WEEK_JOB_STORAGE_KEY);
      } catch (storageError) {
        console.warn('[MobileStrategy] Unable to clear stored job detail', storageError);
      }

      setPendingJobCountdown(null);
      setPendingWeekJob((prev) => prev ? { ...prev, status: 'completed', completedAt: Date.now() } : prev);

      pendingJobCleanupRef.current = setTimeout(() => {
        setPendingWeekJob(null);
      }, 4000);
    } catch (error) {
      console.error('[MobileStrategy] Error refreshing strategy after forced week:', error);
      setPendingJobCountdown(null);
      setPendingWeekJob((prev) => prev ? {
        ...prev,
        status: 'error',
        errorMessage: error?.message || 'Errore durante l\'aggiornamento della strategia'
      } : prev);
    }
  }, [cancelJobTimers, currentCampaign?.id, loadCampaignStrategy, setPostsRefreshKey]);

  const scheduleJob = useCallback((jobDetail) => {
    if (!jobDetail?.campaignId || !currentCampaign?.id || jobDetail.campaignId !== currentCampaign.id) {
      return;
    }

    const createdAt = jobDetail.createdAt || Date.now();
    if (Date.now() - createdAt > FORCE_WEEK_JOB_MAX_AGE_MS) {
      try {
        const stored = sessionStorage.getItem(FORCE_WEEK_JOB_STORAGE_KEY);
        if (stored) {
          const parsedStored = JSON.parse(stored);
          if (parsedStored?.createdAt === jobDetail.createdAt) {
            sessionStorage.removeItem(FORCE_WEEK_JOB_STORAGE_KEY);
          }
        }
      } catch (storageError) {
        console.warn('[MobileStrategy] Unable to clear stale job detail', storageError);
      }
      return;
    }

    if (pendingWeekJob && pendingWeekJob.createdAt === createdAt && pendingWeekJob.campaignId === jobDetail.campaignId) {
      return;
    }

    const resolveDelaySeconds = () => {
      const candidates = [
        jobDetail.response?.job_delay_seconds,
        jobDetail.response?.delay_seconds,
        jobDetail.request?.delay_seconds,
        jobDetail.request?.delaySeconds
      ];

      for (const candidate of candidates) {
        const parsed = Number(candidate);
        if (Number.isFinite(parsed) && parsed >= 0) {
          return parsed;
        }
      }

      return 60;
    };

    const delaySeconds = resolveDelaySeconds();
    const triggerAt = createdAt + delaySeconds * 1000;

    cancelJobTimers();

    const normalizedJob = {
      ...jobDetail,
      createdAt,
      delaySeconds,
      triggerAt,
      status: 'scheduled'
    };

    setPendingWeekJob(normalizedJob);

    const updateCountdown = () => {
      const remaining = Math.max(0, Math.ceil((triggerAt - Date.now()) / 1000));
      setPendingJobCountdown(remaining);
    };

    updateCountdown();

    const timeoutDelay = Math.max(triggerAt - Date.now(), 0);
    pendingJobTimeoutRef.current = setTimeout(() => {
      refreshAfterJob(normalizedJob);
    }, timeoutDelay);

    pendingJobIntervalRef.current = setInterval(updateCountdown, 1000);
  }, [cancelJobTimers, currentCampaign?.id, pendingWeekJob, refreshAfterJob]);

  const handleManualRefresh = useCallback(() => {
    if (pendingWeekJob) {
      refreshAfterJob(pendingWeekJob, { manual: true });
    }
  }, [pendingWeekJob, refreshAfterJob]);

  useEffect(() => {
    if (!currentCampaign?.id) {
      cancelJobTimers();
      setPendingWeekJob(null);
      setPendingJobCountdown(null);
      return;
    }

    try {
      const stored = sessionStorage.getItem(FORCE_WEEK_JOB_STORAGE_KEY);
      if (!stored) {
        return;
      }

      const parsed = JSON.parse(stored);
      if (parsed?.campaignId === currentCampaign.id) {
        scheduleJob(parsed);
      }
    } catch (error) {
      console.warn('[MobileStrategy] Unable to restore forced week job from storage', error);
    }
  }, [cancelJobTimers, currentCampaign?.id, scheduleJob]);

  useEffect(() => {
    const handler = (event) => {
      const detail = event?.detail;
      if (detail?.campaignId) {
        scheduleJob(detail);
      }
    };

    window.addEventListener('mobileForceWeekJob', handler);
    return () => {
      window.removeEventListener('mobileForceWeekJob', handler);
    };
  }, [scheduleJob]);

  useEffect(() => () => {
    cancelJobTimers();
  }, [cancelJobTimers]);

  const pendingWeekInfo = useMemo(() => {
    if (!pendingWeekJob) return null;

    const resolveWeekIndex = () => {
      const candidates = [
        pendingWeekJob.response?.week_index,
        pendingWeekJob.response?.weekIndex,
        pendingWeekJob.request?.week_index,
        pendingWeekJob.request?.weekIndex
      ];

      for (const candidate of candidates) {
        const parsed = Number(candidate);
        if (Number.isInteger(parsed) && parsed >= 0) {
          return parsed;
        }
      }

      return null;
    };

    const weekIndex = resolveWeekIndex();
    const weekLabel = Number.isInteger(weekIndex) ? t('strategy.week.label', { number: weekIndex + 1 }) : null;
    const triggerDate = pendingWeekJob.triggerAt ? new Date(pendingWeekJob.triggerAt) : null;
    const formattedTriggerTime = triggerDate ? timeFormatter.format(triggerDate) : null;

    let message;
    switch (pendingWeekJob.status) {
      case 'refreshing':
      case 'manual-refresh':
        message = t('strategy.forceWeek.messages.refreshing');
        break;
      case 'completed':
        message = t('strategy.forceWeek.messages.completed');
        break;
      case 'error':
        message = pendingWeekJob.errorMessage || t('strategy.forceWeek.messages.error');
        break;
      default: {
        if (pendingJobCountdown !== null && pendingJobCountdown > 0) {
          message = t('strategy.forceWeek.messages.countdown', { seconds: pendingJobCountdown });
        } else {
          message = t('strategy.forceWeek.messages.waiting');
        }
        break;
      }
    }

    return {
      message,
      weekLabel,
      triggerTime: formattedTriggerTime,
      status: pendingWeekJob.status,
      countdown: pendingJobCountdown,
      campaignName: pendingWeekJob.campaignName,
      canForceRefresh: pendingWeekJob.status === 'scheduled' || pendingWeekJob.status === 'error'
    };
  }, [pendingWeekJob, pendingJobCountdown, t, timeFormatter]);

  const showForceWeekBanner = isTestModeEnabled && !!pendingWeekInfo;

  const handleGenerateStrategy = async () => {
    if (!currentCampaign?.id) return;

    try {
      const strategyOptions = {
        user_prompt: 'Generate a comprehensive strategy based on the campaign parameters',
        include_company_analysis: true
      };
      await strategyHook.generateStrategy(currentCampaign.id, strategyOptions);
    } catch (error) {
      console.error('[MobileStrategy] Error generating strategy:', error);
    }
  };

  const selectWeek = (weekIndex) => {
    setSelectedWeek(weekIndex);
  };

  const handleStartPostCreation = async () => {
    if (!currentCampaign?.id) {
      console.log('[MobileStrategy] Cannot start post creation without campaign');
      return;
    }

    try {
      const postOptions = {
        count: 12, // Generate posts based on strategy weeks
        content_type: 'comprehensive'
      };
      
      await strategyHook.startPostCreation(currentCampaign.id, postOptions);
      console.log('[MobileStrategy] Post creation started successfully');
    } catch (error) {
      console.error('[MobileStrategy] Error starting post creation:', error);
    }
  };

  const openImagePreview = (img, gallery = []) => {
    const imageUrl = img?.file_url || img?.url || img?.src || null;
    setImagePreview(imageUrl);

    // Set gallery for carousel
    if (gallery.length > 0) {
      setImageGallery(gallery);
      const currentIndex = gallery.findIndex(item => {
        const itemUrl = item?.file_url || item?.url || item?.src;
        return itemUrl === imageUrl;
      });
      setCurrentImageIndex(currentIndex >= 0 ? currentIndex : 0);
    } else {
      setImageGallery([img]);
      setCurrentImageIndex(0);
    }
  };

  const closeImagePreview = () => {
    setImagePreview(null);
    setImageGallery([]);
    setCurrentImageIndex(0);
  };

  const nextImage = () => {
    if (imageGallery.length > 0) {
      const nextIndex = (currentImageIndex + 1) % imageGallery.length;
      setCurrentImageIndex(nextIndex);
      const nextImg = imageGallery[nextIndex];
      setImagePreview(nextImg?.file_url || nextImg?.url || nextImg?.src || null);
    }
  };

  const prevImage = () => {
    if (imageGallery.length > 0) {
      const prevIndex = (currentImageIndex - 1 + imageGallery.length) % imageGallery.length;
      setCurrentImageIndex(prevIndex);
      const prevImg = imageGallery[prevIndex];
      setImagePreview(prevImg?.file_url || prevImg?.url || prevImg?.src || null);
    }
  };

  // Handle campaign status change with automatic save
  const handleStatusChange = async (newStatus) => {
    if (!currentCampaign?.id) return;

    try {
      // Close dropdown immediately for better UX
      setIsStatusDropdownOpen(false);

      // Update campaign status via API (automatic save)
      if (updateCampaign) {
        await updateCampaign(currentCampaign.id, { status: newStatus });
        console.log('[MobileStrategy] Campaign status updated and saved:', newStatus);
      } else {
        // Fallback to local update only if updateCampaign is not available
        const updatedCampaign = { ...currentCampaign, status: newStatus };
        switchCampaign(updatedCampaign);
        console.warn('[MobileStrategy] Campaign status updated locally only (updateCampaign not available)');
      }
    } catch (error) {
      console.error('[MobileStrategy] Error updating campaign status:', error);
    }
  };

  const shouldShowStrategySkeleton = (
    (backendLoading || strategyLoading || isGeneratingStrategy) &&
    currentCampaign &&
    !backendError &&
    !strategyError
  );

  // If no campaign is selected, show campaign selection
  if (!currentCampaign) {
    return (
      <div className="mobile-strategy">
        <div className="mobile-strategy-content">
          {/* Header */}
          <div className="mobile-strategy-header">
            <h1 className="mobile-strategy-title">{t('strategy.header.title')}</h1>
            <p className="mobile-strategy-subtitle">
              {t('strategy.header.selectSubtitle')}
            </p>
          </div>

          {/* Campaign selector */}
          <div className="mobile-strategy-navbar-wrapper">
            <MobileCampaignSelector
              campaigns={campaigns}
              mode="inline"
              selectionMode="single"
              activeCampaign={currentCampaign}
              onCampaignSelect={handleCampaignSelect}
              showAllOption={false}
              requireConfirmation={false}
            />
          </div>

          {backendLoading && (
            <div className="mobile-strategy-loading">
              <div className="mobile-strategy-spinner" />
            </div>
          )}

          {backendError && (
            <div className="mobile-strategy-error">
              <p>{t('strategy.errors.loadCampaigns', { error: String(backendError) })}</p>
            </div>
          )}

          {/* Empty State */}
          {!backendLoading && !backendError && campaigns.length === 0 && (
            <div className="mobile-strategy-empty">
              <div className="mobile-strategy-empty-icon">
                <img src={iconAi} alt={t('strategy.alt.aiStrategy')} />
              </div>
              <h3>{t('strategy.empty.title')}</h3>
              <p>{t('strategy.empty.description')}</p>
              <button 
                className="mobile-strategy-cta"
                onClick={handleCreateCampaign}
              >
                {t('strategy.empty.cta')}
              </button>
            </div>
          )}

          {/* Campaign Selection Prompt */}
          {!backendLoading && !backendError && campaigns.length > 0 && !currentCampaign && (
            <div className="mobile-strategy-empty">
              <div className="mobile-strategy-empty-icon">
                <img src={iconAi} alt={t('strategy.alt.aiStrategy')} />
              </div>
              <h3>{t('strategy.prompt.title')}</h3>
              <p>{t('strategy.prompt.description')}</p>
            </div>
          )}

          {/* FAB Button */}
          <button
            className="mobile-strategy-fab"
            onClick={handleCreateCampaign}
            aria-label={t('strategy.actions.createCampaign')}
          >
            <div className="mobile-strategy-fab-icon">
              <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 5V19M5 12H19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="mobile-strategy">
      <div className="mobile-strategy-content">
        {/* Campaign Header */}
        <div className="mobile-strategy-header">
          <div className="campaign-title-section">
            <h1 className="mobile-strategy-title">{t('strategy.header.title')}</h1>
            <p className="mobile-campaign-description">
              {t('strategy.header.description')}
            </p>
          </div>
        </div>

        {/* Campaign selector */}
        <div className="mobile-strategy-navbar-wrapper">
          <MobileCampaignSelector
            campaigns={campaigns}
            mode="inline"
            selectionMode="single"
            activeCampaign={currentCampaign}
            onCampaignSelect={handleCampaignSelect}
            showAllOption={false}
            requireConfirmation={false}
          />
        </div>

        {showForceWeekBanner && pendingWeekInfo && (
          <div className={`mobile-force-week-banner status-${pendingWeekInfo.status}`}>
            <div className="banner-content">
              {pendingWeekInfo.weekLabel && (
                <span className="banner-chip">{pendingWeekInfo.weekLabel}</span>
              )}
              <div className="banner-text">
                <p>{pendingWeekInfo.message}</p>
                {pendingWeekInfo.triggerTime && pendingWeekInfo.status === 'scheduled' && (
                  <span className="banner-subtext">
                    {t('strategy.forceWeek.scheduled', { time: pendingWeekInfo.triggerTime })}
                  </span>
                )}
              </div>
            </div>
            <div className="banner-actions">
              {pendingWeekInfo.canForceRefresh && (
                <button
                  className="banner-button"
                  onClick={handleManualRefresh}
                >
                  {pendingWeekInfo.status === 'error'
                    ? t('strategy.forceWeek.actions.retry')
                    : t('strategy.forceWeek.actions.refresh')}
                </button>
              )}
            </div>
          </div>
        )}

        {/* Strategy Loading/Error States */}
        {strategyError && (
          <div className="mobile-strategy-error">
            <p>{t('strategy.errors.loadStrategy', { error: String(strategyError) })}</p>
          </div>
        )}

        {/* Strategy Content */}
        <div className="mobile-strategy-section">
          {shouldShowStrategySkeleton ? (
            renderStrategyContentSkeleton()
          ) : (
            <>
          <div className="section-header">
            <div className="section-header-row">
              <div className="section-header-content">
                <h3>{currentCampaign.name}</h3>
                <p className="section-description">{currentCampaign.description || t('strategy.section.campaignFallback')}</p>
              </div>
              {/* Campaign Status Toggle */}
              <div className="mobile-campaign-status-selector" ref={statusDropdownRef}>
                <button
                  className="mobile-campaign-status-select"
                  onClick={() => setIsStatusDropdownOpen(!isStatusDropdownOpen)}
                >
                  <span className={`status-indicator status-${currentCampaign.status || 'draft'}`}></span>
                  <span>{getStatusLabel(currentCampaign.status)}</span>
                  <svg width="12" height="8" viewBox="0 0 12 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M1 1.5L6 6.5L11 1.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                </button>
                {isStatusDropdownOpen && (
                  <div className="mobile-campaign-status-dropdown">
                    {statusOptions.map(option => (
                      <button
                        key={option.value}
                        className={`mobile-status-dropdown-item ${currentCampaign.status === option.value ? 'active' : ''}`}
                        onClick={() => handleStatusChange(option.value)}
                      >
                        <span className={`status-indicator status-${option.value}`}></span>
                        {option.label}
                        {currentCampaign.status === option.value && (
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                            <path
                              d="M20 6L9 17L4 12"
                              stroke="currentColor"
                              strokeWidth="2.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* General Strategy */}
          <div className="mobile-strategy-block">
            <div className="block-header">
              <h4>{t('strategy.section.general.title')}</h4>
            </div>
            <div className="block-content">
              <p className="strategy-description">
                {strategyData?.desc || t('strategy.section.general.empty')}
              </p>
            </div>
          </div>

          {/* Media & Style Guide */}
          <div className="mobile-strategy-block">
            <div className="block-header">
              <h4>{t('strategy.section.media.title')}</h4>
            </div>
            <div className="block-content">
              {/* Style Guide Images */}
              <div className="style-guide-section">
                <h5>{t('strategy.section.media.styleGuideHeading', { count: strategyImages?.length || 0 })}</h5>
                {strategyImages?.length ? (
                  <div className="mobile-ai-project-thumbnails">
                    <div className="mobile-ai-thumbnails-row">
                      {strategyImages.map((img, idx) => (
                        <div
                          key={idx}
                          className="mobile-ai-thumbnail"
                          onClick={() => openImagePreview(img, strategyImages)}
                        >
                          <img
                            src={img.src || img.url || img.file_url}
                            alt={img.image_alt || t('strategy.section.media.alt.styleGuide')}
                            className="mobile-ai-thumbnail-image"
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="style-guide-empty">
                    <p>{t('strategy.section.media.styleGuideEmpty')}</p>
                  </div>
                )}
              </div>

              {/* Documents Section */}
              <div className="media-section">
                <h5>{t('strategy.section.media.pdfTitle', { count: mediaFiles?.pdf?.length || 0 })}</h5>
                {mediaFiles?.pdf?.length ? (
                  <ul className="media-list">
                    {mediaFiles.pdf.map((file, idx) => (
                      <li key={idx} className="media-item">
                        <span className="media-filename">{file.filename || file.name || t('strategy.section.media.pdfFallback')}</span>
                        <a
                          className="media-action"
                          href={file.url || file.file_url}
                          target="_blank"
                          rel="noreferrer"
                        >{t('strategy.section.media.actions.open')}</a>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <div className="media-empty">
                    <p>{t('strategy.section.media.pdfEmpty')}</p>
                  </div>
                )}
              </div>

              {/* Images Section */}
              <div className="media-section">
                <h5>{t('strategy.section.media.imagesTitle', { count: mediaFiles?.image?.length || 0 })}</h5>
                {mediaFiles?.image?.length ? (
                  <div className="mobile-ai-project-thumbnails">
                    <div className="mobile-ai-thumbnails-row">
                      {mediaFiles.image.map((file, idx) => (
                        <div
                          key={idx}
                          className="mobile-ai-thumbnail"
                          onClick={() => openImagePreview({ url: file.url, file_url: file.file_url }, mediaFiles.image)}
                        >
                          <img
                            src={file.url || file.file_url}
                            alt={file.filename || t('strategy.section.media.imageFallback')}
                            className="mobile-ai-thumbnail-image"
                          />
                        </div>
                      ))}
                      {mediaFiles.image.length > 8 && (
                        <div className="mobile-ai-thumbnail-more">
                          +{mediaFiles.image.length - 8}
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="media-empty">
                    <p>{t('strategy.section.media.imagesEmpty')}</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Weekly Strategy */}
          {strategyData?.weeks && strategyData.weeks.length > 0 && (
            <div className="mobile-strategy-block mobile-weeks-container">
              {/* Week Selector */}
              <div className="week-selector">
                <div className="week-selector-buttons">
                  {weekSteps.map(({ title }, index) => (
                    <button
                      key={`${title}-${index}`}
                      className={`week-selector-btn ${selectedWeek === index ? 'active' : ''}`}
                      onClick={() => selectWeek(index)}
                    >
                      {title}
                    </button>
                  ))}
                </div>
              </div>

              {/* Selected Week Content */}
              <div className="week-content-container">
                <div className="week-content" key={selectedWeek}>
                  <div className="week-header-info">
                    <p className="week-period">
                      {selectedWeekPeriod}
                    </p>
                  </div>

                  <div className="week-description-container">
                    {(!isExtraWeekSelected || (selectedWeekData?.desc && selectedWeekData.desc.trim())) && (
                      <p className="week-description">
                        {isExtraWeekSelected
                          ? selectedWeekData?.desc
                          : selectedWeekData?.desc || t('strategy.week.noDescription')}
                      </p>
                    )}
                  </div>

                  <div className="week-posts">
                    <div className="posts-header">
                      <h5>{t('strategy.posts.header', { count: Array.isArray(weekPosts) ? weekPosts.length : 0 })}</h5>
                    </div>
                    <PostCarousel posts={weekPosts} isLoading={postsLoading} error={postsError} />
                  </div>
                </div>
              </div>
            </div>
          )}
            </>
          )}
        </div>


        {/* Image Preview Modal with Carousel */}
        {imagePreview && (
          <div className="image-preview-modal" role="dialog" aria-modal="true">
            <div className="image-preview-backdrop" onClick={closeImagePreview} />
            <div className="image-preview-content">
              <button className="image-preview-close" onClick={closeImagePreview} aria-label={t('strategy.preview.actions.close')}>×</button>

              {/* Carousel navigation */}
              {imageGallery.length > 1 && (
                <>
                  <button
                    className="image-preview-nav image-preview-prev"
                    onClick={prevImage}
                    aria-label={t('strategy.preview.actions.previous')}
                  >
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M15 18l-6-6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </button>
                  <button
                    className="image-preview-nav image-preview-next"
                    onClick={nextImage}
                    aria-label={t('strategy.preview.actions.next')}
                  >
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M9 18l6-6-6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </button>

                  {/* Image counter */}
                  <div className="image-preview-counter">
                    {t('strategy.preview.counter', {
                      current: currentImageIndex + 1,
                      total: imageGallery.length
                    })}
                  </div>
                </>
              )}

              <img src={imagePreview} alt={t('strategy.preview.alt.image')} />
            </div>
          </div>
        )}

        {/* FAB Button */}
        <button
          className="mobile-strategy-fab"
          onClick={handleCreateCampaign}
          aria-label={t('strategy.actions.createCampaign')}
        >
          <div className="mobile-strategy-fab-icon">
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 5V19M5 12H19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
        </button>
      </div>
    </div>
  );
};

export default MobileStrategy;
